import { useState, useRef, useCallback } from 'react'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  horizontalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, X, Loader2, ImageIcon, ZoomIn } from 'lucide-react'
import { toast } from 'sonner'
import { adminApi } from '@/api/admin'

const MAX_IMAGES = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp']

interface Props {
  images: string[]
  onChange: (urls: string[]) => void
  label: string
}

function SortableImage({
  id, url, onRemove, onPreview,
}: {
  id: string; url: string; onRemove: () => void; onPreview: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative w-20 h-20 rounded-lg border border-gray-200 overflow-hidden group cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <img src={url} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
        <button
          onClick={(e) => { e.stopPropagation(); onPreview() }}
          className="p-1 bg-white/90 rounded-full"
          type="button"
        >
          <ZoomIn className="w-3 h-3 text-gray-700" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="p-1 bg-white/90 rounded-full"
          type="button"
        >
          <X className="w-3 h-3 text-red-500" />
        </button>
      </div>
    </div>
  )
}

export function BuildupImageUploader({ images, onChange, label }: Props) {
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const remaining = MAX_IMAGES - images.length
    if (remaining <= 0) {
      toast.error(`최대 ${MAX_IMAGES}장까지 업로드할 수 있습니다.`)
      return
    }

    const validFiles: File[] = []
    for (let i = 0; i < Math.min(files.length, remaining); i++) {
      const file = files[i]
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: 지원하지 않는 파일 형식입니다.`)
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: 5MB를 초과합니다.`)
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    setUploading(true)
    try {
      const results = await Promise.all(
        validFiles.map((file) => adminApi.uploadImage(file, 'buildup'))
      )
      const newUrls = results.map((r) => r.url)
      onChange([...images, ...newUrls])
    } catch {
      toast.error('이미지 업로드에 실패했습니다.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [images, onChange])

  const handleRemove = useCallback((index: number) => {
    onChange(images.filter((_, i) => i !== index))
  }, [images, onChange])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = images.indexOf(active.id as string)
      const newIndex = images.indexOf(over.id as string)
      onChange(arrayMove(images, oldIndex, newIndex))
    }
  }, [images, onChange])

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1.5 mb-1">
        <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs text-gray-500">{label} ({images.length}/{MAX_IMAGES})</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images} strategy={horizontalListSortingStrategy}>
            {images.map((url, idx) => (
              <SortableImage
                key={url}
                id={url}
                url={url}
                onRemove={() => handleRemove(idx)}
                onPreview={() => setPreviewUrl(url)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {images.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span className="text-[10px] mt-0.5">추가</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {previewUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center cursor-pointer"
          onClick={() => setPreviewUrl(null)}
        >
          <img
            src={previewUrl}
            alt="Preview"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 p-2 bg-white/90 rounded-full"
            onClick={() => setPreviewUrl(null)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  )
}
